import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, ButtonV2 } from '@asigna/kit';
import cx from 'classnames';
import { SafeAvatar, Tooltip } from 'components';
import { isActivatedPro, useSidebarState } from 'hooks';
import useAddress from 'hooks/use-address';
import {
  useProSubAccountsDialog,
  useSafes,
  useSafesBalances,
  useSelectedSubAccounts,
  useSubAccountCreateDialog,
} from 'store';
import { IClaimMultiSignWallet, IMultiSignWallet } from 'types';
import { isPendingSafe, shortenAddress } from 'utils/helper';
import { isNewSubAccountError } from 'utils/subaccounts';

import checkedIcon from './util/images/checked.svg';
import clockIcon from './util/images/clock.svg';
import emptyIcon from './util/images/empty.svg';
import lockIcon from './util/images/lock.svg';

type Safe = IClaimMultiSignWallet | IMultiSignWallet;

type SafeCardProps = {
  safe: Safe;
  onClick: (safe: Safe, isSubaccount?: boolean) => void;
  onClose: () => void;
};

export const SafeCard: React.FC<SafeCardProps> = ({ safe, onClick, onClose }) => {
  const { currentSafeId } = useSidebarState();
  const walletAccount = useAddress();
  const { safes } = useSafes();
  const { setProSubAccountsDialogOpen } = useProSubAccountsDialog();
  const { setSubAccountCreateDialogOpen } = useSubAccountCreateDialog();

  const { safesBalances } = useSafesBalances();

  const { _id, address, name, threshold } = safe;

  const isPending = isPendingSafe(safe);

  const isJoined = isPending
    ? safe.owners.some((owner) => owner.address === walletAccount && owner.verified)
    : false;

  const subaccounts = useMemo(() => {
    return safes.filter((item) => item.parrentId === safe._id);
  }, [safe, safes]);

  const isCurrentSafe =
    currentSafeId === safe._id || subaccounts.some((acc) => acc._id === currentSafeId);

  const currentAddressSafe =
    subaccounts.find((acc) => acc._id === currentSafeId)?.address || safe.address;

  const owners = safe.owners.length;

  const handleCreateSubAccount = () => {
    selectSubAccount(safe._id, safe._id);
    history.push(`/vault/${safe.address}/dashboard`);
    setSubAccountCreateDialogOpen(safe._id);
  };

  const disabledSubaccountError = isNewSubAccountError(safe);
  const isProActivated = isActivatedPro(safe as unknown as IMultiSignWallet, safes);
  const { selectSubAccount } = useSelectedSubAccounts();
  const isShowSubAccount = isCurrentSafe && subaccounts.length > 0;
  const history = useHistory();

  const getSafeUsdBalance = (address: string) => {
    if (!address) return '-';
    const balance = safesBalances[address];

    // if (address === 'SM3XWZ6ESYX9KBC9T202ZX8QGFNWP6J3696HNCWF7') {
    //   console.log('============================================');
    //   console.log('safesBalances', safesBalances);
    //   console.log('balance', balance);
    // }

    if (!balance) return '$0.00';

    return `$${balance.toFixed(2)}`;
  };

  return (
    <div
      className={cx(
        'flex flex-col rounded-[12px] w-full border-[.5px] cursor-pointer transition-all',
        isShowSubAccount ? 'px-8 py-8' : isPending ? 'px-16 py-24' : 'px-16 py-16',
        isCurrentSafe && !isPending
          ? 'border-primary bg-primary-10 hover:border-primary-30 hover:bg-primary-5'
          : !isPending
            ? 'border-black-4 bg-black-2 hover:border-primary-20 hover:bg-primary-5'
            : 'border-black-4 bg-[linear-gradient(119deg,_#353535_0%,_#19191A_70.52%)] hover:border-primary-30'
      )}
    >
      <div
        className={cx(
          'flex items-center w-full gap-12',
          isShowSubAccount && 'justify-center bg-white-3 rounded-[4px] py-16'
        )}
        onClick={() => {
          if (
            subaccounts.length === 0 ||
            (currentSafeId !== safe._id && currentSafeId !== safe.parrentId)
          ) {
            onClick(safe, subaccounts.length > 0);
          }
        }}
      >
        <div className="relative">
          <div className="flex items-center justify-center rounded-[50%] w-32 h-32 border-[.5px] border-white-5 bg-white-10">
            <SafeAvatar size="32px" address={_id} />
          </div>
          <div
            className={cx(
              'absolute top-[-6px] left-[2px] rounded-[8px] px-4 h-16 flex items-center text-[11px] text-white',
              Boolean(address) ? 'bg-primary' : 'bg-black-3'
            )}
          >
            {threshold}/{owners}
          </div>
        </div>
        <div className="flex flex-col gap-4 items-start">
          <div className="flex items-center gap-[6px]">
            <p className="text-white text-r14 max-w-[132px] text-ellipsis overflow-hidden">
              {name}
            </p>
            {!Boolean(address) && <img src={clockIcon} width={14} height={14} alt="clock icon" />}
          </div>
          {!isShowSubAccount && <p className="text-regular text-r12">{shortenAddress(address)}</p>}
          {subaccounts.length > 0 && !isCurrentSafe && (
            <div className="flex items-center h-28 px-8 bg-white-10 rounded-[14px] text-r13 mt-4">
              {subaccounts.length} {subaccounts.length === 1 ? 'Sub-Account' : 'Sub-Accounts'}
            </div>
          )}
        </div>
        {Boolean(address) && !isShowSubAccount && (
          <p className="text-regular text-r12 ml-auto">{getSafeUsdBalance(address)}</p>
        )}
        {isPending && (
          <div className="flex items-center gap-4 ml-auto">
            <div className={cx('w-4 h-4 rounded-[4px]', isJoined ? 'bg-success' : 'bg-error')} />
            <p className={cx('text-r12', isJoined ? 'text-success' : 'text-error')}>
              {isJoined ? 'Joined' : 'Not joined'}
            </p>
          </div>
        )}
      </div>
      {isShowSubAccount && (
        <div className="flex flex-col gap-12 py-12">
          {[safe, ...subaccounts]?.map((account) => (
            <div key={account._id}>
              <div
                className="flex w-full items-center gap-12 px-8"
                onClick={(e) => {
                  e.stopPropagation();

                  if (!isProActivated) {
                    return;
                  }
                  history.push(`/vault/${account.address}/dashboard`);
                  selectSubAccount(safe._id, account._id);
                  onClose();
                }}
              >
                <img
                  src={currentAddressSafe === account.address ? checkedIcon : emptyIcon}
                  alt=""
                />

                <div className="flex flex-col">
                  <div className="flex items-center gap-4">
                    <span className="text-white text-m14">
                      {!account.parrentId ? 'Main Account' : account.name}
                    </span>
                    {!isProActivated && account.parrentId && <img src={lockIcon} alt="" />}
                  </div>
                  <span className="text-regular text-r12">{shortenAddress(account.address)}</span>
                </div>
                <p className="text-regular text-r12 ml-auto">
                  {getSafeUsdBalance(account.address)}
                </p>
              </div>
              <div className="bg-white-10 h-[0.5px] mx-[-8px] mt-12" />
            </div>
          ))}
        </div>
      )}
      {isCurrentSafe && !isPending && (
        <div className="w-full relative">
          {isProActivated && !!disabledSubaccountError && (
            <div className={cx('w-full', subaccounts.length === 0 ? 'mt-8 pl-44' : 'px-8')}>
              <Tooltip
                tooltipStyle={{
                  top: '48px',
                  left: '0',
                  transform: 'translateX(0)',
                  width: '100%',
                  height: 'fit-content',
                }}
                style={{ width: '100%' }}
                content={disabledSubaccountError}
              >
                <Button
                  onClick={handleCreateSubAccount}
                  colorType="secondary"
                  message="Create Sub-Account"
                  size="md"
                  fullWidth
                  disabled={!!disabledSubaccountError}
                  className={cx('bg-white-10', subaccounts.length > 0 ? 'mb-8' : '')}
                />
              </Tooltip>
            </div>
          )}
          {isProActivated && !disabledSubaccountError && (
            <div className={cx('w-full', subaccounts.length === 0 ? 'mt-8 pl-44' : 'px-8')}>
              <Button
                onClick={handleCreateSubAccount}
                colorType="secondary"
                message="Create Sub-Account"
                size="md"
                fullWidth
                disabled={!!disabledSubaccountError}
                className={cx('bg-white-10', subaccounts.length > 0 ? 'mb-8' : '')}
              />
            </div>
          )}

          {!isProActivated && !isPending && (
            <>
              {!isShowSubAccount && <div className="bg-white-10 h-[0.5px] mx-[-16px] mt-12 mb-4" />}
              <div className="flex flex-col items-center gap-8 px-8 my-8">
                <div className="flex items-center gap-4">
                  <span className="text-m13 text-regular">Asigna</span>
                  <div className="text-center font-bold text-[13px] leading-[20px] text-black-0 w-[43px] h-24 rounded-[12px] bg-regular flex items-center justify-center">
                    Pro
                  </div>
                </div>
                <span className="text-center text-m13 mb-4">
                  Activate Asigna Pro to enable switching between sub-accounts.
                </span>
                <ButtonV2
                  className={cx(
                    'mt-4 w-full',
                    'bg-[linear-gradient(103deg,_#FFE341_0%,_#F7E47D_24.5%,_#F1C855_48.99%,_#FFE2A8_73.49%,_#FFE970_97.99%)]',
                    'hover:bg-[linear-gradient(104deg,_#FFE341_0%,_#F7E47D_35.79%,_#F1C855_71.57%,_#FFE2A8_107.36%,_#FFE970_143.14%)]'
                  )}
                  onClick={() => setProSubAccountsDialogOpen(safe._id)}
                >
                  <span className="text-[#493B15] font-extrabold">Activate Subscription</span>
                </ButtonV2>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
